import { useState, useEffect } from 'react';

export const useInfiniteLoading = <T>(
  completeCollection: T[],
  increment: number,
  identifierKey: keyof T, // unique key like id or timestamp in each item
  delay: number | undefined = 0
) => {
  const [data, setData] = useState<{ collection: T[]; isLoading: boolean }>({ collection: [], isLoading: false });

  useEffect(() => {
    const currentLimitIndex = data.collection.length
      ? completeCollection.findIndex(
          (item) => item[identifierKey] === data.collection[data.collection.length - 1][identifierKey]
        ) + 1
      : 0;
    const limitIndex = Math.max(currentLimitIndex, increment);
    setData((data) => ({
      ...data,
      collection:
        completeCollection.length < limitIndex ? [...completeCollection] : [...completeCollection.slice(0, limitIndex)],
    }));
  }, [JSON.stringify(completeCollection)]);

  const setLoadMore = () => {
    setData((data) => ({ ...data, isLoading: true }));
  };

  const loadMoreData = () => {
    const cLength = data.collection.length;
    const completeLength = completeCollection.length;
    const incrementData = completeLength - cLength > increment ? increment : completeLength - cLength;

    return setTimeout(() => {
      setData((data) => ({
        collection: [...data.collection, ...completeCollection.slice(cLength, cLength + incrementData)],
        isLoading: false,
      }));
    }, delay);
  };

  useEffect(() => {
    let timeout: NodeJS.Timeout;
    if (data.isLoading) {
      timeout = loadMoreData();
    }
    return () => clearTimeout(timeout);
  }, [data.isLoading]);

  return { ...data, setLoadMore, hasMore: data.collection.length < completeCollection.length };
};
